import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../api/client";
import { colors, scoreColor } from "../theme/colors";

interface CostResponse {
  costs: {
    elevenlabs: number;
    whisper: number;
    zimage: number;
    kling: number;
    total: number;
  };
  counts: {
    tts_calls: number;
    whisper_calls: number;
    zimage_calls: number;
    kling_calls: number;
  };
}
interface CadenceResponse {
  by_week: Array<Record<string, string | number>>;
  by_category: Record<string, number>;
  total: number;
}

async function getCosts(): Promise<CostResponse> {
  const r = await fetch("/api/analytics/costs");
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
async function getCadence(): Promise<CadenceResponse> {
  const r = await fetch("/api/analytics/cadence");
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

const CHART_COLORS = [
  colors.blue,
  colors.purple,
  colors.magenta,
  colors.red,
  colors.blueDeep,
  colors.purpleDeep,
];

export function Analytics() {
  const scores = useQuery({
    queryKey: ["analytics_scores"],
    queryFn: api.analytics.scores,
  });
  const cadence = useQuery({
    queryKey: ["analytics_cadence"],
    queryFn: getCadence,
  });
  const costs = useQuery({
    queryKey: ["analytics_costs"],
    queryFn: getCosts,
  });

  // Brand score → time series
  const scoreSeries = (scores.data ?? [])
    .slice()
    .reverse()
    .map((s, i) => ({
      idx: i + 1,
      date: s.date,
      score: s.brand_score,
      category: s.category,
    }));

  const costPie =
    costs.data && costs.data.costs.total > 0
      ? [
          { name: "ElevenLabs", value: costs.data.costs.elevenlabs },
          { name: "Whisper", value: costs.data.costs.whisper },
          { name: "Z-Image", value: costs.data.costs.zimage },
          { name: "Kling 3.0", value: costs.data.costs.kling },
        ].filter((s) => s.value > 0)
      : [];

  return (
    <div className="space-y-12">
      <div>
        <h1 className="headline text-6xl text-brand-text-dark">Analytics</h1>
        <p className="mt-2 text-sm text-brand-text-dark/60">
          Markenscore über Zeit, Cadence pro Woche, geschätzte Kosten pro Service.
        </p>
      </div>

      <Section title="Brand score over time">
        <div className="h-72 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          {scoreSeries.length === 0 ? (
            <Empty />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={scoreSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8DFCC" />
                <XAxis dataKey="idx" tick={{ fontSize: 11 }} />
                <YAxis domain={[40, 100]} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  formatter={(v: unknown) => [`${v}`, "Score"]}
                  labelFormatter={(_, payload) => {
                    const p = payload?.[0]?.payload as { date: string; category: string } | undefined;
                    return p ? `${p.date} · ${p.category}` : "";
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke={colors.blue}
                  strokeWidth={3}
                  dot={(props) => {
                    const { cx, cy, payload } = props as {
                      cx: number;
                      cy: number;
                      payload: { score: number };
                    };
                    return (
                      <circle
                        key={`${cx}-${cy}`}
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill={scoreColor(payload.score)}
                        stroke="white"
                        strokeWidth={2}
                      />
                    );
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Section>

      <Section title="Cadence (posts per week)">
        <div className="h-72 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          {cadence.data && cadence.data.by_week.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cadence.data.by_week}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8DFCC" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {Object.keys(cadence.data.by_category).map((cat, i) => (
                  <Bar
                    key={cat}
                    dataKey={cat}
                    stackId="a"
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty />
          )}
        </div>
      </Section>

      <Section title="Cost estimate">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="h-64 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5 lg:col-span-2">
            {costPie.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={costPie}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={80}
                    label={(props: unknown) => {
                      const p = props as { name: string; value: number };
                      return `${p.name}: $${p.value.toFixed(3)}`;
                    }}
                  >
                    {costPie.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: unknown) => `$${Number(v).toFixed(4)}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty message="No paid API calls logged yet." />
            )}
          </div>
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5">
            <div className="text-xs font-bold uppercase tracking-wider text-brand-text-dark/60">
              Total estimated spend
            </div>
            <div className="mt-2 text-4xl font-bold text-brand-text-dark">
              ${costs.data?.costs.total.toFixed(2) ?? "0.00"}
            </div>
            {costs.data ? (
              <dl className="mt-4 space-y-1.5 text-xs">
                <Row label="TTS calls" value={costs.data.counts.tts_calls} />
                <Row label="Whisper calls" value={costs.data.counts.whisper_calls} />
                <Row label="Z-Image calls" value={costs.data.counts.zimage_calls} />
                <Row label="Kling video calls" value={costs.data.counts.kling_calls} />
              </dl>
            ) : null}
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-text-dark/50">
        {title}
      </h2>
      {children}
    </section>
  );
}
function Empty({ message = "Noch keine Daten." }: { message?: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-brand-text-dark/40">
      {message}
    </div>
  );
}
function Row({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-brand-text-dark/60">{label}</span>
      <span className="font-bold tabular-nums">{value}</span>
    </div>
  );
}
